import type { CSSProperties, ReactNode, SyntheticEvent } from 'react';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

import { RestrictToElement } from '@dnd-kit/dom/modifiers';
import { useSortable } from '@dnd-kit/react/sortable';
import AddOutlined from '@mui/icons-material/AddOutlined';
import ClearOutlined from '@mui/icons-material/ClearOutlined';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import Box from '@mui/material/Box';
import Tab from '@mui/material/Tab';
import MuiTabs from '@mui/material/Tabs';
import type { Sx } from '@/shared/types/uiTypes';

import { TooltipIconButton } from '@/shared/ui/primitives/IconButton';

interface Tab {
  name: string;
  id?: string;
  disabled?: boolean;
  label?: ReactNode;
  closable?: boolean;
  onClose?: (value: number) => void;
  tag?: ReactNode;
  sortableHandleRef?: React.RefObject<HTMLElement | null>;
}

/** Visual density preset controlling tab height and label font size. @default 'default' */
type TabsVariant = 'default' | 'compact';

const TAB_VARIANTS: Record<
  TabsVariant,
  { height: number; labelFontSize?: number; minTabWidth: number }
> = {
  default: { height: 48, minTabWidth: 180 },
  compact: { height: 36, labelFontSize: 14, minTabWidth: 0 }
};

interface CustomTabsProps {
  tabs: Tab[];
  activeTab: number;
  onChange?: (index: number) => void;
  onAddTab?: () => void;
  onTabClick?: (tabName: string) => void;
  height?: number;
  /** Minimum width per tab. Defaults to the variant's preset (180px for 'default', 0 for 'compact'). */
  minTabWidth?: number;
  offset?: number;
  maxTabWidth?: number | 'unset';
  scrollButtons?: boolean | 'auto';
  disableRipple?: boolean;
  /** Horizontal alignment of each tab's label text. @default 'center' */
  labelAlign?: 'center' | 'left';
  /** Visual density preset controlling tab height and label font size. @default 'default' */
  variant?: TabsVariant;
  /** Overrides the variant's label font size for a one-off case. */
  labelFontSize?: number | string;
}

type MuiTabsInjectedTabProps = {
  fullWidth?: boolean;
  indicator?: ReactNode;
  onChange?: (event: SyntheticEvent, value: unknown) => void;
  selected?: boolean;
  selectionFollowsFocus?: boolean;
  textColor?: 'inherit' | 'primary' | 'secondary';
  value?: unknown;
};

interface SortableTabItemProps extends MuiTabsInjectedTabProps {
  id: string;
  index: number;
  handle?: React.RefObject<HTMLElement | null>;
  container: React.RefObject<HTMLDivElement | null>;
  label: ReactNode;
  height: number;
  minTabWidth: number;
  maxTabWidth: number | 'unset';
  disableFocusRipple?: boolean;
  disableRipple?: boolean;
  disabled?: boolean;
  ariaControls: string;
  tabId: string;
  name: string;
  closable?: boolean;
  onSelectTab?: (index: number) => void;
  activeTab: number;
  onClick: () => void;
}

const tabsRootSx: Sx = {
  display: 'flex',
  alignItems: 'center',
  gap: 0.5,
  minWidth: 0
};

// Wraps the scroller + overlaid scroll buttons. Buttons are positioned absolutely
// inside it, so showing/hiding either of them never changes this element's size —
// unlike flex siblings, an overlay can't push the tab list around or leave a
// reserved blank slot behind when it's hidden.
const scrollableTabsWrapperSx: Sx = {
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  minWidth: 0,
  maxWidth: 'calc(100% - var(--tabs-offset))'
};

const tabsSx: Sx = {
  minWidth: 0,
  width: '100%',
  minHeight: 'var(--tabs-height)',
  height: 'var(--tabs-height)'
};

const scrollButtonSx: Sx = {
  position: 'absolute',
  top: 0,
  width: 32,
  minWidth: 32,
  zIndex: 1,
  backgroundColor: 'gx.tileLight',
  color: 'gx.textDisabled',
  '&:hover': {
    color: 'white',
    backgroundColor: '#161b40'
  }
};

// The 4px border in the page background color visually separates the overlaid
// button from the tab content it's covering, on the side facing the tab list.
const scrollButtonPrevSx: Sx = {
  left: 0,
  borderRight: '2px solid',
  borderColor: 'background.default'
};
const scrollButtonNextSx: Sx = {
  right: 0,
  borderLeft: '2px solid',
  borderColor: 'background.default'
};

const AUTO_SCROLL_SPEED_PX_PER_FRAME = 6;
const SCROLL_STEP_PX = 200;
// scrollWidth/clientWidth are integer-rounded per tab; rounding several tabs
// independently can accumulate to a few px of phantom overflow even when the
// tab strip visually fits, which would otherwise leave a scroll arrow stuck on.
const OVERFLOW_TOLERANCE_PX = 3;

const getShowScrollButtons = (scrollButtonsProp: boolean | 'auto', hasOverflow: boolean) =>
  scrollButtonsProp === true || (scrollButtonsProp === 'auto' && hasOverflow);

const tabSx: Sx = {
  alignItems: 'flex-start',
  textTransform: 'initial',
  height: 'var(--tab-height)',
  minWidth: 'var(--tab-min-width)',
  maxWidth: 'var(--tab-max-width)',
  minHeight: 'var(--tab-height)',
  p: 1,
  '&:not(:last-child)': { mr: '2px' }
};

const zeroMinWidthTabSx: Sx = { pr: 3 };

const tabLabelSx: Sx = {
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  width: '100%',
  height: '100%',
  textAlign: 'center'
};

const clickableTabLabelSx: Sx = {
  ...tabLabelSx,
  cursor: 'pointer',
  borderRadius: '4px'
};

const fullWidthLabelSx: Sx = { flexBasis: '100%' };

const closeLabelSx: Sx = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%'
};

const closeButtonSx: Sx = {
  height: 46,
  position: 'absolute',
  right: 0,
  backgroundColor: 'transparent',
  ':hover': {
    backgroundColor: 'action.hover'
  }
};

const addButtonSx: Sx = {
  width: 32,
  minWidth: 32,
  backgroundColor: 'gx.tileLight'
};

const addIconSx: Sx = {
  fontSize: '14px !important',
  color: 'gx.textDisabled'
};

const scrollArrowIconSx: Sx = {
  fontSize: '26px !important'
};

const SortableTabItem = ({
  id,
  index,
  handle,
  container,
  label,
  height,
  minTabWidth,
  maxTabWidth,
  disableFocusRipple,
  disableRipple,
  disabled,
  ariaControls,
  tabId,
  name,
  closable,
  onSelectTab,
  activeTab,
  onClick,
  ...muiTabProps
}: SortableTabItemProps) => {
  const internalMuiTabProps = muiTabProps as React.ComponentProps<typeof Tab>;

  const { ref } = useSortable({
    id,
    index,
    handle,
    type: 'dashboard-tab',
    accept: ['dashboard-tab'],
    modifiers: [RestrictToElement.configure({ element: container.current })]
  });

  return (
    <Tab
      ref={ref}
      {...internalMuiTabProps}
      aria-controls={ariaControls}
      disabled={disabled}
      disableFocusRipple={disableFocusRipple}
      disableRipple={disableRipple}
      id={tabId}
      label={
        <Box
          role='presentation'
          sx={tabLabelSx}
          onMouseDownCapture={event => {
            if (onSelectTab && activeTab !== index) {
              event.stopPropagation();
              onSelectTab(index);
            }
          }}
        >
          <Box sx={fullWidthLabelSx}>{label}</Box>
        </Box>
      }
      style={
        {
          '--tab-height': `${height}px`,
          '--tab-min-width': `${minTabWidth}px`,
          '--tab-max-width': maxTabWidth === 'unset' ? 'unset' : `${maxTabWidth}px`
        } as CSSProperties
      }
      sx={minTabWidth === 0 && closable ? [tabSx, zeroMinWidthTabSx] : tabSx}
      onClick={onClick}
    />
  );
};

const CloseButton = ({
  label,
  name,
  onClose,
  index,
  labelAlign = 'center',
  labelFontSize
}: {
  label: ReactNode | undefined;
  index: number;
  name: string;
  onClose?: (value: number) => void;
  labelAlign?: 'center' | 'left';
  labelFontSize?: number | string;
}) => {
  const labelOverrideSx: Sx = {
    ...(labelAlign === 'left' && { justifyContent: 'flex-start' }),
    ...(labelFontSize !== undefined && { fontSize: labelFontSize })
  };

  return (
    <>
      <Box sx={[closeLabelSx, labelOverrideSx]}>{label || name}</Box>
      <TooltipIconButton
        component='span'
        sx={closeButtonSx}
        title='Remove'
        onClick={e => {
          e.stopPropagation();
          if (onClose) onClose(index);
        }}
      >
        <ClearOutlined />
      </TooltipIconButton>
    </>
  );
};

const CustomTabs = ({
  tabs,
  activeTab,
  onChange,
  onAddTab,
  onTabClick,
  variant = 'default',
  height = TAB_VARIANTS[variant].height,
  minTabWidth = TAB_VARIANTS[variant].minTabWidth,
  maxTabWidth = 'unset',
  offset = 0,
  scrollButtons = false,
  disableRipple = false,
  labelAlign = 'center',
  labelFontSize = TAB_VARIANTS[variant].labelFontSize,
  ...tabProps
}: CustomTabsProps) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const autoScrollDirectionRef = useRef<0 | 1 | -1>(0);

  const [scrollState, setScrollState] = useState({
    canScrollPrev: false,
    canScrollNext: false,
    hasOverflow: false
  });

  const getScroller = useCallback(
    () => containerRef.current?.querySelector<HTMLDivElement>('.MuiTabs-scroller'),
    []
  );

  // MuiTabs-list, not MuiTabs-flexContainer (renamed in MUI v9). Only used to
  // detect content-size changes via ResizeObserver — its own
  // getBoundingClientRect() is clamped to the scroller's visible width when
  // overflowing, not the tabs' true combined width, so it can't be used to
  // measure overflow itself (see recalculateScrollState).
  const getTabsList = useCallback(
    () => containerRef.current?.querySelector<HTMLDivElement>('.MuiTabs-list'),
    []
  );

  const getTabElements = useCallback(
    () => Array.from(containerRef.current?.querySelectorAll<HTMLElement>('.MuiTab-root') ?? []),
    []
  );

  const stopAnimation = useCallback(() => {
    autoScrollDirectionRef.current = 0;
    if (animationFrameRef.current !== null) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
  }, []);

  const runAutoScroll = useCallback(() => {
    const scroller = getScroller();
    if (scroller && autoScrollDirectionRef.current !== 0) {
      scroller.scrollLeft += autoScrollDirectionRef.current * AUTO_SCROLL_SPEED_PX_PER_FRAME;
    }
    animationFrameRef.current = requestAnimationFrame(runAutoScroll);
  }, [getScroller]);

  const startAutoScroll = useCallback(
    (direction: 1 | -1) => {
      stopAnimation();
      autoScrollDirectionRef.current = direction;
      animationFrameRef.current = requestAnimationFrame(runAutoScroll);
    },
    [runAutoScroll, stopAnimation]
  );

  const scrollByStep = useCallback(
    (direction: 1 | -1) => {
      const scroller = getScroller();
      if (!scroller) return;

      stopAnimation();
      scroller.scrollBy({ left: direction * SCROLL_STEP_PX, behavior: 'smooth' });
    },
    [getScroller, stopAnimation]
  );

  const recalculateScrollState = useCallback(() => {
    const scroller = getScroller();
    if (!scroller) return;

    const tabEls = getTabElements();
    if (tabEls.length === 0) {
      setScrollState({ canScrollPrev: false, canScrollNext: false, hasOverflow: false });
      return;
    }

    const { scrollLeft, clientWidth } = scroller;
    // scroller.scrollWidth is unreliable right after tabs are removed: Chromium
    // keeps reporting the old (larger) scrollable extent and lets it decay back
    // down over the following second or so — seemingly tied to its scrollbar
    // hide/resize animation. MuiTabs-list's own rect isn't a substitute either:
    // when the tabs genuinely overflow, the list's own box gets clamped to the
    // scroller's visible width rather than growing to fit its (overflowing)
    // children. Measuring the actual span from the first tab's left edge to the
    // last tab's right edge sidesteps both — each tab keeps its true rendered
    // size regardless of the parent's clamped box or the scroller's stale metric.
    const contentWidth =
      tabEls[tabEls.length - 1].getBoundingClientRect().right -
      tabEls[0].getBoundingClientRect().left;
    const canScrollPrev = scrollLeft > OVERFLOW_TOLERANCE_PX;
    const canScrollNext = scrollLeft < contentWidth - clientWidth - OVERFLOW_TOLERANCE_PX;

    if (!canScrollPrev && autoScrollDirectionRef.current === -1) stopAnimation();
    if (!canScrollNext && autoScrollDirectionRef.current === 1) stopAnimation();

    setScrollState({
      canScrollPrev,
      canScrollNext,
      hasOverflow: contentWidth > clientWidth + OVERFLOW_TOLERANCE_PX
    });
  }, [getScroller, getTabElements, stopAnimation]);

  useLayoutEffect(() => {
    if (scrollButtons === false) return undefined;

    const scroller = getScroller();
    if (!scroller) return undefined;

    // Measure and commit before paint so tab add/remove/reorder never flashes a
    // stale arrow state (shown-then-hidden or vice versa) — only the final,
    // correct state is ever visible.
    recalculateScrollState();

    const resizeObserver = new ResizeObserver(recalculateScrollState);
    // The scroller's own box only changes when the surrounding container
    // resizes. It does NOT change when a tab's label content grows or shrinks
    // (e.g. a tab first opened with a short code, later renamed to its full
    // title) — that only changes the list's content width, not the scroller's
    // box, so it would never re-trigger this observer on its own. Watching the
    // tab list too catches those content-driven width changes.
    resizeObserver.observe(scroller);
    const list = getTabsList();
    if (list) resizeObserver.observe(list);
    scroller.addEventListener('scroll', recalculateScrollState, { passive: true });

    return () => {
      resizeObserver.disconnect();
      scroller.removeEventListener('scroll', recalculateScrollState);
    };
  }, [scrollButtons, tabs.length, getScroller, getTabsList, recalculateScrollState]);

  useEffect(() => stopAnimation, [stopAnimation]);

  const showScrollButtons = getShowScrollButtons(scrollButtons, scrollState.hasOverflow);

  const handleChange = (event: SyntheticEvent, newValue: number) => {
    if (onChange) onChange(newValue);
  };

  const tabsStyle = {
    '--tabs-height': `${height}px`,
    '--tabs-offset': `${offset}px`
  } as CSSProperties;
  const tabStyle = {
    '--tab-height': `${height}px`,
    '--tab-min-width': `${minTabWidth}px`,
    '--tab-max-width': maxTabWidth === 'unset' ? 'unset' : `${maxTabWidth}px`
  } as CSSProperties;

  const labelOverrideSx: Sx = {
    ...(labelAlign === 'left' && { textAlign: 'left' }),
    ...(labelFontSize !== undefined && { fontSize: labelFontSize })
  };

  return (
    <Box sx={tabsRootSx}>
      <Box sx={scrollableTabsWrapperSx}>
        <MuiTabs
          ref={containerRef}
          aria-label='tabs'
          scrollButtons={false}
          style={tabsStyle}
          sx={tabsSx}
          value={activeTab}
          variant='scrollable'
          onChange={handleChange}
        >
          {tabs.map(
            ({ name, id, label, disabled, closable, onClose, tag, sortableHandleRef }, index) => {
              const tabLabel = closable ? (
                <CloseButton
                  index={index}
                  label={label}
                  labelAlign={labelAlign}
                  labelFontSize={labelFontSize}
                  name={name}
                  onClose={onClose}
                />
              ) : (
                <Box sx={fullWidthLabelSx}>{label || name}</Box>
              );

              if (id && sortableHandleRef) {
                return (
                  <SortableTabItem
                    key={id}
                    activeTab={activeTab}
                    ariaControls={`tabpanel-${name}`}
                    closable={closable}
                    container={containerRef}
                    disableFocusRipple={disableRipple}
                    disableRipple={disableRipple}
                    disabled={disabled}
                    handle={sortableHandleRef}
                    height={height}
                    id={id}
                    index={index}
                    label={
                      <>
                        {tabLabel}
                        {tag}
                      </>
                    }
                    maxTabWidth={maxTabWidth}
                    minTabWidth={minTabWidth}
                    name={name}
                    tabId={`tab-${name}`}
                    onClick={() => onTabClick?.(name)}
                    onSelectTab={onChange}
                  />
                );
              }

              return (
                <Tab
                  key={id ?? `tab-${name}-${index}`}
                  aria-controls={`tabpanel-${name}`}
                  disabled={disabled}
                  disableFocusRipple={disableRipple}
                  disableRipple={disableRipple}
                  id={`tab-${name}`}
                  label={
                    <Box
                      role='presentation'
                      sx={[clickableTabLabelSx, labelOverrideSx]}
                      onMouseDownCapture={event => {
                        if (onChange && activeTab !== index) {
                          event.stopPropagation();
                          onChange(index);
                        }
                      }}
                    >
                      {tabLabel}
                      {tag}
                    </Box>
                  }
                  style={tabStyle}
                  sx={minTabWidth === 0 && closable ? [tabSx, zeroMinWidthTabSx] : tabSx}
                  onClick={() => onTabClick?.(name)}
                  {...tabProps}
                />
              );
            }
          )}
        </MuiTabs>
        {showScrollButtons && scrollState.canScrollPrev && (
          <TooltipIconButton
            sx={[scrollButtonSx, scrollButtonPrevSx, { height }]}
            title='Scroll tabs left'
            onClick={() => scrollByStep(-1)}
            onMouseEnter={() => startAutoScroll(-1)}
            onMouseLeave={stopAnimation}
          >
            <KeyboardArrowLeft sx={scrollArrowIconSx} />
          </TooltipIconButton>
        )}
        {showScrollButtons && scrollState.canScrollNext && (
          <TooltipIconButton
            sx={[scrollButtonSx, scrollButtonNextSx, { height }]}
            title='Scroll tabs right'
            onClick={() => scrollByStep(1)}
            onMouseEnter={() => startAutoScroll(1)}
            onMouseLeave={stopAnimation}
          >
            <KeyboardArrowRight sx={scrollArrowIconSx} />
          </TooltipIconButton>
        )}
      </Box>
      {onAddTab && (
        <TooltipIconButton
          sx={addButtonSx}
          title='Add new page'
          onClick={onAddTab}
        >
          <AddOutlined sx={addIconSx} />
        </TooltipIconButton>
      )}
    </Box>
  );
};

export type { Tab };
export default CustomTabs;
